import { useEffect, useRef, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useLocation } from 'react-router-dom';
import './index.css';
import SideBar from '../../components/global/SideBar';
import { Banners } from '../../components/videos/Banners';
import { fetchVideos, type CategorizedVideos, type VideoFilter } from '../../services/video.service';
import type { VideoInterface } from '../../types/video.interface';
import Filters from '../../components/videos/Filters';
import { CategorizedVideoList } from '../../components/videos/CategorizedVideoList';
import { Loader } from '@beholos/holoscommon';
import PaginatedVideoList from '../../components/videos/PaginatedVideoList';
import SaasCategoryFilter from '../../components/videos/SaasCategoryFilter';

export default function Videos() {
  usePageTitle('Videos - Holos');
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchUrlParam = new URLSearchParams(location.search).get('query');
  const [selectedFilters, setSelectedFilters] = useState({
    search: searchUrlParam || '',
    category: '',
    teacher: '',
    partner: '',
    duration: '',
    equipment: '',
    saasCategory: ''
  });
  const [videos, setVideos] = useState<VideoInterface[]>([]);
  const [categorizedVideos, setCategorizedVideos] = useState<CategorizedVideos[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState<VideoFilter[]>([]);
  const [saasCategories, setSaasCategories] = useState<VideoFilter[]>([]);
  const [teachers, setTeachers] = useState<string[]>([]);
  const [partners, setPartners] = useState<VideoFilter[]>([]);
  const [equipment, setEquipment] = useState<VideoFilter[]>([]);

  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const saasCategoryParam = new URLSearchParams(location.search).get('saasCategory');
  const [pendingSaasCategoryName, setPendingSaasCategoryName] = useState<string | null>(saasCategoryParam);

  useEffect(() => {
    setPage(1);
    setLoading(true);
    setLoadingMore(false);
    setVideos([]);
    setCategorizedVideos([]);
    fetchVideos(selectedFilters, 1).then((response) => {
      if (selectedFilters.category) {
        setCategorizedVideos([]);
        setVideos(response.data as VideoInterface[]);
        setTotalPages(response.totalPages);
      } else {
        setVideos([]);
        setCategorizedVideos(response.data as CategorizedVideos[]);
      }

      const availableFilters = response.availableFilters[0];
      if (categories.length === 0) {
        setCategories(availableFilters.categories);
        setSaasCategories(availableFilters.saasCategories);
      }
      setTeachers(availableFilters.teachers);
      setPartners(availableFilters.studios);
      setEquipment(availableFilters.equipment);
    }).catch((error) => {
      console.error('Error fetching videos:', error);
      setLoading(false);
    }).finally(() => {
      setLoading(false);
    });
  }, [selectedFilters]);

  // Map saasCategory name from query param to its ID once filters are loaded
  useEffect(() => {
    if (pendingSaasCategoryName && saasCategories.length > 0 && !selectedFilters.saasCategory) {
      const match = saasCategories.find(sc => sc.name?.toLowerCase() === pendingSaasCategoryName.toLowerCase());
      if (match) {
        setSelectedFilters({ ...selectedFilters, saasCategory: match._id });
      }
      setPendingSaasCategoryName(null);
    }
  }, [pendingSaasCategoryName, saasCategories, selectedFilters]);

  const handleSelectCategory = (categoryId: string) => {
    setSelectedFilters({ ...selectedFilters, category: categoryId })
    videoWrapperRef.current?.scrollTo({ top: 500, behavior: 'smooth' });
  }

  const onLoadMore = async () => {
    if (page < totalPages) {
      setPage(prevPage => prevPage + 1);
      setLoadingMore(true);
      try {
        const response = await fetchVideos(selectedFilters, page + 1);
        setVideos(prevVideos => [...prevVideos, ...(response.data as VideoInterface[])]);
      } catch (error) {
        console.error('Error loading more videos:', error);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      <div className="flex-1 h-full overflow-y-auto" ref={videoWrapperRef}>
        <Banners />

        <Filters
          categories={categories}
          teachers={teachers}
          partners={partners}
          equipment={equipment}
          selectedFilters={selectedFilters}
          onFilterChange={
            (filters) => setSelectedFilters({
              ...filters,
              saasCategory: selectedFilters.saasCategory
            })
          }
          onReset={() => {
            setSelectedFilters({
              search: '',
              category: '',
              teacher: '',
              partner: '',
              duration: '',
              equipment: '',
              saasCategory: ''
            });
            videoWrapperRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />

        <div className="w-full max-w-7xl mx-auto px-4 py-6">
          <h2 className="font-juana text-xl font-bold mb-4">
            Videos On-Demand por categoría
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {saasCategories.map(category => (
              <SaasCategoryFilter
                key={category._id}
                selected={selectedFilters.saasCategory === category._id}
                onClick={() => setSelectedFilters({ ...selectedFilters, saasCategory: category._id })}
                background={`https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/category${category.name.toLowerCase()}001.jpg`}
                saasCategory={category.name}
              />
            ))
            }
          </div>
        </div>

        {loading ? <Loader /> : <div className="w-full max-w-7xl mx-auto px-4 py-6">
          {categorizedVideos.length > 0 ? (
            <CategorizedVideoList
              selectCategory={handleSelectCategory}
              categorizedVideos={categorizedVideos} />
          ) : (
            <PaginatedVideoList
              videos={videos}
              onLoadMore={onLoadMore}
              hasMore={page < totalPages}
              loading={loadingMore}
              videoWrapperRef={videoWrapperRef}
            />
          )}
        </div>}

        {!loading && videos.length === 0 && categorizedVideos.length === 0 && (
          <div className="flex justify-center items-center pt-20 pb-60">
            <p className="text-gray-500 text-center">
              No hay resultados con estos filtros.
              <br />
              Actualice o restablezca su búsqueda para obtener mejores resultados
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
